import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Tooltip from '@atoms/Tooltip/Tooltip';
import { IImage } from '@models/IImage';
import ProfilePictureSizeEnum from '@models/enums/ProfilePictureSizeEnum';
import TooltipPositionEnum from '@models/enums/TooltipPositionEnum';
import classNames from 'classnames';

interface Props {
  readonly backgroundColourClassName?: string;
  readonly displayCheck?: boolean;
  readonly displayOutline?: boolean;
  readonly displayOutlineColor?: string;
  readonly iconToolTip?: string;
  readonly initials?: string;
  readonly size: ProfilePictureSizeEnum;
  readonly thumbnail?: IImage;
  readonly tooltipBackgroundColor?: string;
  readonly dataTestId?: string;
}

const ProfilePicture = ({
  backgroundColourClassName = 'bg-grey-100 dark:bg-grey-600',
  displayCheck = false,
  displayOutline = false,
  displayOutlineColor = 'border-yellow',
  iconToolTip,
  initials,
  size,
  thumbnail,
  tooltipBackgroundColor,
  dataTestId = 'Profile-Picture',
}: Props) => {
  const imageSizes = {
    [ProfilePictureSizeEnum.Size30]: 'w-[30px] h-[30px]',
    [ProfilePictureSizeEnum.Size38]: 'w-[38px] h-[38px]',
    [ProfilePictureSizeEnum.Size41]: 'w-[41px] h-[41px]',
    [ProfilePictureSizeEnum.Size50]: 'w-[50px] h-[50px]',
    [ProfilePictureSizeEnum.Size76]: 'w-[76px] h-[76px]',
    [ProfilePictureSizeEnum.Size120]: 'w-[120px] h-[120px]',
  };

  const initialsSizes = {
    [ProfilePictureSizeEnum.Size30]: 'small-text',
    [ProfilePictureSizeEnum.Size38]: 'small-text',
    [ProfilePictureSizeEnum.Size41]: 'small-text',
    [ProfilePictureSizeEnum.Size50]: 'body-four',
    [ProfilePictureSizeEnum.Size76]: 'h6',
    [ProfilePictureSizeEnum.Size120]: 'h4',
  };

  const check = {
    [ProfilePictureSizeEnum.Size30]: '0.625rem',
    [ProfilePictureSizeEnum.Size38]: '0.59375rem',
    [ProfilePictureSizeEnum.Size41]: '0.854rem',
    [ProfilePictureSizeEnum.Size50]: '1rem',
    [ProfilePictureSizeEnum.Size76]: '1.125rem',
    [ProfilePictureSizeEnum.Size120]: '1.5rem',
  };

  const checkOffset = {
    [ProfilePictureSizeEnum.Size30]: 'right-0.5 -bottom-px',
    [ProfilePictureSizeEnum.Size38]: 'right-0.5 -bottom-1',
    [ProfilePictureSizeEnum.Size41]: 'right-1 -bottom-0.5',
    [ProfilePictureSizeEnum.Size50]: 'right-0 bottom-0',
    [ProfilePictureSizeEnum.Size76]: 'right-1 bottom-0',
    [ProfilePictureSizeEnum.Size120]: 'right-2 bottom-2',
  };

  return (
    <div className={classNames('relative shrink-0', imageSizes[size])}>
      <div
        className={classNames(
          {
            [`border-2 ${displayOutlineColor}`]: displayOutline,
            [`${backgroundColourClassName}`]: !thumbnail,
          },
          `h-full w-full rounded-full`
        )}
      >
        {thumbnail ? (
          <Image
            image={thumbnail}
            imageClasses="rounded-full w-full h-full"
            sizes="300px"
            dataTestId={`${dataTestId}-Image`}
          />
        ) : (
          initials && (
            <div className="flex h-full w-full items-center justify-center">
              <span
                className={classNames(
                  'uppercase text-grey-450 dark:text-grey-100',
                  initialsSizes[size]
                )}
                data-testid={`${dataTestId}-Initials`}
              >
                {initials}
              </span>
            </div>
          )
        )}
      </div>

      {displayCheck && (
        <div
          className={classNames(
            'tooltip-parent absolute flex',
            checkOffset[size]
          )}
        >
          <Icon
            name="check"
            colorClassName="text-black-100 bg-yellow rounded-full p-[1px]"
            size={check[size]}
          />
          {iconToolTip && (
            <Tooltip
              label={iconToolTip}
              position={TooltipPositionEnum.Top}
              backgroundColor={tooltipBackgroundColor}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProfilePicture;
